/**
 * OAuth authorization-code de-duplication for the SSO callback.
 *
 * Security (CodeQL js/clear-text-storage-of-sensitive-data, TES-698): the raw
 * OAuth `code` is sensitive and must NEVER be written to browser storage in
 * clear text. We persist only non-reversible SHA-256 fingerprints (`sha256:` +
 * 64 hex chars) so a duplicate callback (mobile double-processing / reload) can
 * be detected without ever storing the secret.
 *
 * The write path persists ONLY fingerprints freshly produced by the digest
 * helper this session — it never re-writes a value read back from storage, so
 * there is no getItem -> setItem clear-text round-trip. The read path is still
 * consulted (getItem only) so dedup survives a reload.
 */

// Storage key intentionally avoids sensitive tokens (e.g. "code"): the stored
// values are non-reversible SHA-256 fingerprints, never the raw OAuth code.
const PROCESSED_FINGERPRINTS_KEY = "oauth_processed_fingerprints";
const CODE_FINGERPRINT_PREFIX = "sha256:";

// A persisted entry is only ever a non-reversible SHA-256 fingerprint. Validating
// against this shape acts as a barrier: nothing that is not a one-way digest can
// be read from or written to storage.
const FINGERPRINT_PATTERN = new RegExp(`^${CODE_FINGERPRINT_PREFIX}[a-f0-9]{64}$`);

// In-memory accumulator of the SHA-256 fingerprints produced *this session*.
let sessionFingerprints: string[] = [];

function isFingerprint(value: unknown): value is string {
  return typeof value === "string" && FINGERPRINT_PATTERN.test(value);
}

function digestToHex(digest: ArrayBuffer): string {
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function readProcessedFingerprints(): string[] {
  try {
    const processed = sessionStorage.getItem(PROCESSED_FINGERPRINTS_KEY);
    const parsed = processed ? JSON.parse(processed) : [];
    // Only well-formed SHA-256 fingerprints survive — never clear-text data.
    return Array.isArray(parsed) ? parsed.filter(isFingerprint) : [];
  } catch {
    return [];
  }
}

/** SHA-256 fingerprint of an OAuth code, or null if WebCrypto is unavailable. */
async function createCodeFingerprint(code: string): Promise<string | null> {
  if (!globalThis.crypto?.subtle) {
    return null;
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(code));
  return `${CODE_FINGERPRINT_PREFIX}${digestToHex(digest)}`;
}

/**
 * Reset the in-memory accumulator for a fresh callback handling. The persisted
 * storage value remains the cross-reload dedup backstop.
 */
export function resetProcessedCodes(): void {
  sessionFingerprints = [];
}

/** True if this OAuth code was already processed (this session or a prior load). */
export async function isCodeAlreadyProcessed(code: string): Promise<boolean> {
  const fingerprint = await createCodeFingerprint(code);
  if (!fingerprint) {
    return false;
  }
  return (
    sessionFingerprints.includes(fingerprint) || readProcessedFingerprints().includes(fingerprint)
  );
}

/** Record this OAuth code as processed by persisting only its SHA-256 digest. */
export async function markCodeAsProcessed(code: string): Promise<void> {
  const fingerprint = await createCodeFingerprint(code);
  if (!fingerprint) {
    return;
  }
  try {
    // Persist only non-reversible SHA-256 fingerprints freshly produced this
    // session — never the raw code, and never a value read back from storage.
    sessionFingerprints = [...sessionFingerprints, fingerprint].filter(isFingerprint).slice(-5);
    sessionStorage.setItem(PROCESSED_FINGERPRINTS_KEY, JSON.stringify(sessionFingerprints));
  } catch {
    // Ignore storage errors
  }
}
